import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  EmptyState,
  Field,
  Icon,
  Panel,
  Select,
  Tag,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatPesos } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import {
  ACCOUNT_KIND_LABEL,
  ACTOR_SOURCE_LABEL,
  REVERSAL_REASONS,
  REVERSAL_REJECTIONS,
  whyNotReversible,
  type ReversalRequestDetail,
  type TransactionDetailPayload,
} from './detail.ts';

/**
 * ONE LEDGER ROW, OPENED.
 *
 * The ledger has been listable since the console was built and no row could be
 * opened, so an operator looking at a mis-posted deposit could read its ref and
 * its signed amount and nothing else — not which accounts it moved money
 * between, not who caused it, and not whether anything had already been done
 * about it. This drawer is the answer to all three, and the place a reversal is
 * raised and decided.
 *
 * EVERY CONTROL THAT IS DISABLED SAYS WHY, IN VISIBLE TEXT, and never in a
 * `title` attribute — browsers suppress the tooltip on a disabled control, so a
 * `title` there is a reason nobody can read. Each disabled button carries
 * `aria-describedby` pointing at the paragraph that explains it, so the reason
 * is announced with the control rather than found by accident.
 *
 * THE FOUR REASONS a control here is disabled are different facts and read
 * differently: the role does not hold the capability; this posting is not the
 * kind that can be reversed; a reversal already exists; or you are the person
 * who raised the request and therefore cannot be the one to decide it.
 */

/** "29 Aug 2026, 21:14" — 24-hour, so 09:14 is never ambiguous on a money screen. */
const stamp = new Intl.DateTimeFormat('en-PH', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const at = (iso: string) => stamp.format(new Date(iso));

const STATUS_TONE = (status: string) =>
  status === 'SETTLED' ? 'accent' : status === 'FAILED' || status === 'REVERSED' ? 'quiet' : 'outline';

/** What the operator is doing in the action area, if anything. */
type Mode = 'idle' | 'raising' | 'rejecting';

export interface TransactionDetailProps {
  /** The row that was clicked, or null when the drawer is closed. */
  transactionId: string | null;
  onClose: () => void;
  /** Called after a write lands, so the list behind can re-read itself. */
  onChanged: () => void;
}

export function TransactionDetail({ transactionId, onClose, onChanged }: TransactionDetailProps) {
  const { allowed, session } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [detail, setDetail] = useState<TransactionDetailPayload | null>(null);
  const [loading, setLoading] = useState(false);
  /**
   * Whether the last read FAILED, as distinct from having nothing to show.
   *
   * The same distinction the list makes: an empty panel under a dismissed error
   * modal reads as "this posting has no entries", which on a money screen is a
   * claim about the LEDGER. "There are none" and "we could not ask" are
   * different facts and only one of them is safe to act on.
   */
  const [loadFailed, setLoadFailed] = useState(false);

  const [mode, setMode] = useState<Mode>('idle');
  const [reasonCode, setReasonCode] = useState(REVERSAL_REASONS[0]!.code);
  const [note, setNote] = useState('');
  const [rejectCode, setRejectCode] = useState(REVERSAL_REJECTIONS[0]!.code);
  const [rejectNote, setRejectNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    (id: string) => {
      setLoading(true);
      api
        .get<TransactionDetailPayload>(`/api/transactions/${id}`)
        .then((d) => {
          setDetail(d);
          setLoadFailed(false);
        })
        .catch((error: unknown) => {
          /* Cleared rather than left standing: a stale posting under a failure
             notice reads as the one that was asked for. */
          setDetail(null);
          setLoadFailed(true);
          showError({
            title: 'Could not open this transaction',
            message:
              error instanceof ApiRequestError
                ? error.message
                : 'The transaction could not be read. Try again shortly.',
            ...(error instanceof ApiRequestError ? { code: error.code } : {}),
          });
        })
        .finally(() => setLoading(false));
    },
    [showError],
  );

  useEffect(() => {
    if (!transactionId) return;
    /* Every piece of per-row state is dropped when the row changes. A reason
       typed against one posting must never travel to the next. */
    setDetail(null);
    setLoadFailed(false);
    setMode('idle');
    setReasonCode(REVERSAL_REASONS[0]!.code);
    setNote('');
    setRejectCode(REVERSAL_REJECTIONS[0]!.code);
    setRejectNote('');
    load(transactionId);
  }, [transactionId, load]);

  const open = transactionId !== null;
  const canRaise = allowed('funds.adjust');
  const canDecide = allowed('funds.approve');
  const role = session?.role ?? 'your role';
  const meId = session?.id ?? null;

  const request: ReversalRequestDetail | null = detail?.openReversalRequest ?? null;
  const blocked = detail ? whyNotReversible(detail) : null;
  const isMine = request !== null && request.initiatedBy === meId;

  /** Fire a write, report a failure in the modal, and re-read on success. */
  const send = useCallback(
    async (path: string, body: unknown, success: string) => {
      if (!transactionId) return;
      setBusy(true);
      try {
        await api.post(`/api/transactions/${transactionId}${path}`, body);
        toast(success);
        setMode('idle');
        setNote('');
        setRejectNote('');
        load(transactionId);
        onChanged();
      } catch (error: unknown) {
        /* A failure ALWAYS opens the modal, never inline text. The one thing an
           operator must not do is act on a reversal they believe happened. */
        showError({
          title: 'The reversal did not go through',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The platform could not be reached. Nothing was changed.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      } finally {
        setBusy(false);
      }
    },
    [transactionId, toast, load, onChanged, showError],
  );

  /* ── What the action area offers, and why it might not ─────────────────── */

  const raiseDisabledReason = !canRaise
    ? `Raising a reversal needs the funds.adjust capability, which ${role} does not hold. It is the same capability that submits a manual balance adjustment, and it is deliberately not the one that approves either of them.`
    : blocked;

  /*
   * WHERE THE TWO-PERSON RULE ACTUALLY LIVES — named here, not on screen.
   *
   * The sentence below used to say "the rule is a CHECK constraint on
   * wallet.ledger_reversal", which is true and useless to the person reading
   * it: an operator cannot look that up, ask anybody for it, or repeat it to
   * support. The constraint is `ledger_reversal_maker_is_not_checker` in
   * packages/db/sql/440_ledger_reversal.sql, and it refuses to store any
   * request whose approver is also its maker.
   *
   * WHAT THE OPERATOR NEEDS from that fact is the part worth keeping: the
   * refusal is structural rather than this screen being polite about it, so
   * there is no version of asking again, no senior override, and nothing the
   * console could be talked into. That is why the wording says the request
   * cannot be STORED that way instead of saying the screen will not allow it —
   * one is a fact about the platform, the other is a fact about a button.
   */
  const decideDisabledReason = !canDecide
    ? `Deciding a reversal needs the funds.approve capability, which ${role} does not hold — the same capability that releases a payout.`
    : isMine
      ? `You raised ${request?.ref ?? 'this request'}, so you cannot be the one to decide it. A second admin holding funds.approve has to. This is not the screen being cautious: the platform cannot store a request where the same person both raised it and approved it, so the rule holds even if this screen were bypassed.`
      : null;

  const noteTooShort = note.trim().length < 10;
  const rejectNoteTooShort = rejectNote.trim().length < 10;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={detail?.ref ?? (loadFailed ? 'Could not be read' : 'Loading…')}
      subtitle={
        detail
          ? [detail.player?.name, detail.player?.ref, detail.brandName].filter(Boolean).join(' · ') ||
            'no player · no brand'
          : undefined
      }
    >
      {loading && !detail ? (
        <div className="skeleton" style={{ height: '220px' }}>
          &nbsp;
        </div>
      ) : loadFailed || !detail ? (
        <EmptyState icon="warning-circle" title="This transaction could not be read">
          The request failed, so nothing below is a statement about the ledger. Close this and try
          again — and if it keeps happening, quote the reference from the error dialog.
        </EmptyState>
      ) : (
        <div className="stack">
          {/* ── What it is ──────────────────────────────────────────────── */}
          <div className="row">
            <Tag tone="neutral">{detail.kind.replaceAll('_', ' ').toLowerCase()}</Tag>
            <Tag tone={STATUS_TONE(detail.status)}>{detail.status.replaceAll('_', ' ').toLowerCase()}</Tag>
            {detail.providerErrorCode ? (
              <Tag tone="quiet">provider {detail.providerErrorCode}</Tag>
            ) : null}
            {detail.reversedBy ? <Tag tone="outline">reversed</Tag> : null}
            {detail.reverses ? <Tag tone="outline">is a reversal</Tag> : null}
          </div>

          <div className="stat">
            <div className="stat-label">Signed against the player&rsquo;s balance</div>
            <div className="stat-value lg tabular">
              {formatPesos(BigInt(detail.amount), { sign: true })}
            </div>
            <div className="text-subtle" style={{ fontSize: '11.5px', marginTop: 'var(--space-2)' }}>
              Occurred {at(detail.occurredAt)} · recorded {at(detail.createdAt)}
              {detail.externalRef ? ` · provider reference ${detail.externalRef}` : ''}
              {detail.playerCashBalance !== null
                ? ` · this player now holds ${formatPesos(BigInt(detail.playerCashBalance))} in cash`
                : ' · no player, so there is no balance to report'}
            </div>
          </div>

          {/* ── What moved ──────────────────────────────────────────────── */}
          <Panel title={`What moved · ${detail.entryCount} entr${detail.entryCount === 1 ? 'y' : 'ies'}`}>
            {detail.entries.length === 0 ? (
              <EmptyState icon="scales" title="No entries">
                This posting records an attempt and moved no money. A FAILED or PENDING movement
                writes no ledger entry at all, which is why its amount above is zero.
              </EmptyState>
            ) : (
              <>
                <div className="stack" style={{ gap: 'var(--space-2)' }}>
                  {detail.entries.map((e) => (
                    <div key={e.id} className="row" style={{ justifyContent: 'space-between' }}>
                      <div>
                        {ACCOUNT_KIND_LABEL[e.accountKind] ?? e.accountKind}
                        <div className="text-subtle" style={{ fontSize: '11px' }}>
                          {[e.accountPlayerRef, e.accountBrandName].filter(Boolean).join(' · ') ||
                            'operator account'}
                        </div>
                      </div>
                      <span className="tabular">{formatPesos(BigInt(e.amount), { sign: true })}</span>
                    </div>
                  ))}
                </div>
                <div
                  className={detail.entryTotal === '0' ? 'text-subtle' : 'notice'}
                  style={{ fontSize: '11.5px', marginTop: 'var(--space-3)' }}
                >
                  {detail.entryTotal === '0' ? (
                    <>
                      {/* A DEFERRABLE constraint trigger checks the sum at commit. */}
                      The entries sum to ₱0.00, which is what double-entry requires and what the
                      ledger itself checks before any posting is saved. A reversal mirrors every one
                      of them, so it balances for the same reason.
                    </>
                  ) : (
                    <>
                      <Icon name="warning-circle" />
                      <div>
                        <strong>These entries do not sum to zero.</strong> They total{' '}
                        {formatPesos(BigInt(detail.entryTotal), { sign: true })}, which the
                        ledger&rsquo;s own check should have made impossible. This needs a data
                        investigation, and a reversal will be refused.
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </Panel>

          {/* ── Who caused it ───────────────────────────────────────────── */}
          <Panel title="Who caused this">
            {detail.actors.length === 0 ? (
              <EmptyState icon="user-circle" title="No operator record">
                Nothing in this platform names a person behind this posting. Bets, wins and bonus
                conversions are written by the game and bonus engines with no request row behind
                them, so there is no actor to show — and inventing a plausible one would be worse
                than saying so.
              </EmptyState>
            ) : (
              <div className="stack" style={{ gap: 'var(--space-3)' }}>
                {detail.actors.map((a) => (
                  <div key={`${a.source}-${a.sourceRef}`}>
                    <strong>{ACTOR_SOURCE_LABEL[a.source] ?? a.source}</strong>{' '}
                    <span className="tabular">{a.sourceRef}</span>
                    <div className="text-subtle" style={{ fontSize: '11.5px' }}>
                      {a.playerInitiated
                        ? 'The player asked for this and the rail settled it. No operator decided anything, and there is nobody in this console to name.'
                        : [
                            a.initiatedByLabel
                              ? `Raised by ${a.initiatedByLabel}`
                              : a.initiatedBy
                                ? `Raised by ${a.initiatedBy}`
                                : 'No maker recorded',
                            a.approvedByLabel
                              ? `approved by ${a.approvedByLabel}`
                              : a.approvedBy
                                ? `approved by ${a.approvedBy}`
                                : null,
                            a.decidedAt ? `on ${at(a.decidedAt)}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* ── The reversal relationship, both ways ────────────────────── */}
          <Panel title="Reversal">
            <div className="stack">
              {detail.reverses ? (
                <div className="notice notice-quiet">
                  <Icon name="arrow-u-left-up" />
                  <div>
                    This posting <strong>reverses</strong>{' '}
                    <span className="tabular">{detail.reverses.ref}</span>, a{' '}
                    {detail.reverses.kind.replaceAll('_', ' ').toLowerCase()} of{' '}
                    {formatPesos(BigInt(detail.reverses.amount), { sign: true })} on{' '}
                    {at(detail.reverses.occurredAt)}. The original was not altered — it stands
                    exactly as it was posted, and this movement is what corrects it.
                  </div>
                </div>
              ) : null}

              {detail.reversedBy ? (
                <div className="notice notice-quiet">
                  <Icon name="arrow-u-right-down" />
                  <div>
                    This posting was <strong>reversed by</strong>{' '}
                    <span className="tabular">{detail.reversedBy.ref}</span> on{' '}
                    {at(detail.reversedBy.occurredAt)}, which moved{' '}
                    {formatPesos(BigInt(detail.reversedBy.amount), { sign: true })} against the
                    player. A posting is reversed once.
                  </div>
                </div>
              ) : null}

              {detail.reversalRequests.length > 0 ? (
                <div className="stack" style={{ gap: 'var(--space-3)' }}>
                  {detail.reversalRequests.map((r) => (
                    <div key={r.id}>
                      <div className="row">
                        <span className="tabular">{r.ref}</span>
                        <Tag
                          tone={
                            r.status === 'APPROVED'
                              ? 'accent'
                              : r.status === 'REJECTED'
                                ? 'quiet'
                                : 'outline'
                          }
                        >
                          {r.status.replaceAll('_', ' ').toLowerCase()}
                        </Tag>
                      </div>
                      <div className="text-subtle" style={{ fontSize: '11.5px' }}>
                        {r.reasonCode.replaceAll('_', ' ').toLowerCase()} — {r.reasonNote}
                      </div>
                      <div className="text-subtle" style={{ fontSize: '11.5px' }}>
                        Raised by {r.makerLabel ?? r.initiatedBy} on {at(r.createdAt)}
                        {r.status === 'APPROVED' && r.approvedAt
                          ? ` · approved by ${r.checkerLabel ?? r.approvedBy} on ${at(r.approvedAt)} · posted ${r.reversalTransactionRef}`
                          : ''}
                        {r.status === 'REJECTED' && r.approvedAt
                          ? ` · refused by ${r.checkerLabel ?? r.approvedBy} on ${at(r.approvedAt)}${
                              r.rejectedReasonNote ? ` — ${r.rejectedReasonNote}` : ''
                            }`
                          : ''}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* ── The action area ─────────────────────────────────────── */}
              {request === null ? (
                mode === 'raising' ? (
                  <div className="stack">
                    {/*
                     * THE REASON IS A CLOSED LIST, AND THE LIST LIVES IN CODE.
                     *
                     * `wallet.ledger_reversal.reason_code` is a plain varchar
                     * with no reference table behind it, deliberately: the
                     * platform has no reason-code lookup table at all, and
                     * `manual_adjustment.reason_code` and
                     * `withdrawal.rejection_reason_code` are the same shape, so
                     * inventing one for this feature alone would leave three
                     * columns with three different notions of what a reason
                     * code is. The closed set therefore lives in
                     * REVERSAL_REASONS here and in the route's own schema, and
                     * that agreement is the whole reason a report can group
                     * reversals by reason at all.
                     *
                     * The hint deliberately says only the operator's half of
                     * that — pick from the list because the reason gets
                     * counted. Which column holds it is ours to know.
                     */}
                    <Field
                      label="Why is this being reversed?"
                      hint="Pick the closest one. This is a fixed list because the reason you choose is what reports count reversals by, and a freely worded one could not be counted alongside the rest — the box below is where you say what actually happened."
                    >
                      {({ id }) => (
                        <Select
                          id={id}
                          value={reasonCode}
                          onChange={(e) => setReasonCode(e.target.value)}
                        >
                          {REVERSAL_REASONS.map((r) => (
                            <option key={r.code} value={r.code}>
                              {r.label}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                    <Field
                      label="What happened"
                      error={
                        note.length > 0 && noteTooShort
                          ? 'At least 10 characters. The code is a form number; this is the half a person reads a year from now.'
                          : undefined
                      }
                      hint="Required, and recorded on the request itself — not only in the audit log. A reversal without a reason is the thing this queue exists to make impossible."
                    >
                      {({ id, describedBy, invalid }) => (
                        <textarea
                          id={id}
                          className="input"
                          rows={3}
                          maxLength={1000}
                          value={note}
                          aria-describedby={describedBy}
                          aria-invalid={invalid}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Name the evidence — a statement line, a ticket, a duplicate reference"
                        />
                      )}
                    </Field>
                    <div className="row">
                      <Button onClick={() => setMode('idle')} disabled={busy}>
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        disabled={busy || noteTooShort}
                        aria-describedby={noteTooShort ? 'reversal-note-gap' : undefined}
                        onClick={() =>
                          void send(
                            '/reversal',
                            { reasonCode, note: note.trim() },
                            `${detail.ref} — reversal requested. It moves no money until a second admin approves it.`,
                          )
                        }
                      >
                        {busy ? 'Submitting…' : 'Submit for approval'}
                      </Button>
                    </div>
                    {noteTooShort ? (
                      <p id="reversal-note-gap" className="text-subtle" style={{ fontSize: '11.5px' }}>
                        Submit is disabled until the reason is at least 10 characters. The platform
                        refuses a shorter one, and refuses a blank one again underneath, so a
                        request without a reason cannot be recorded at all.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      disabled={raiseDisabledReason !== null || busy}
                      aria-describedby={raiseDisabledReason ? 'reverse-gap' : undefined}
                      onClick={() => setMode('raising')}
                    >
                      <Icon name="arrow-u-left-up" />
                      Reverse this transaction
                    </Button>
                    {raiseDisabledReason ? (
                      /* THE TRUE REASON, IN VISIBLE TEXT. Never a `title`: a
                         browser suppresses the tooltip on a disabled control, so
                         a reason written there is one nobody can read. */
                      <p id="reverse-gap" className="text-subtle" style={{ fontSize: '11.5px' }}>
                        {raiseDisabledReason}
                      </p>
                    ) : (
                      <p className="text-subtle" style={{ fontSize: '11.5px' }}>
                        A reversal is a NEW posting that mirrors every entry above and points back
                        at this one. Nothing here is edited or deleted. It needs a reason, and a
                        second admin holding <code>funds.approve</code> has to approve it before any
                        money moves.
                      </p>
                    )}
                  </>
                )
              ) : mode === 'rejecting' ? (
                <div className="stack">
                  <Field label="Why is the reversal refused?">
                    {({ id }) => (
                      <Select
                        id={id}
                        value={rejectCode}
                        onChange={(e) => setRejectCode(e.target.value)}
                      >
                        {REVERSAL_REJECTIONS.map((r) => (
                          <option key={r.code} value={r.code}>
                            {r.label}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  <Field
                    label="Note"
                    error={
                      rejectNote.length > 0 && rejectNoteTooShort
                        ? 'At least 10 characters — the person who raised it is owed the reason.'
                        : undefined
                    }
                    hint="Required. It closes the request and is recorded against it; the posting stays reversible, so a better-argued request can still be raised."
                  >
                    {({ id, describedBy, invalid }) => (
                      <textarea
                        id={id}
                        className="input"
                        rows={3}
                        maxLength={1000}
                        value={rejectNote}
                        aria-describedby={describedBy}
                        aria-invalid={invalid}
                        onChange={(e) => setRejectNote(e.target.value)}
                      />
                    )}
                  </Field>
                  <div className="row">
                    <Button onClick={() => setMode('idle')} disabled={busy}>
                      Cancel
                    </Button>
                    <Button
                      disabled={busy || rejectNoteTooShort}
                      onClick={() =>
                        void send(
                          '/reversal/reject',
                          { reasonCode: rejectCode, note: rejectNote.trim() },
                          `${request.ref} refused. Nothing moved, and ${detail.ref} can still be reversed.`,
                        )
                      }
                    >
                      {busy ? 'Refusing…' : 'Refuse the reversal'}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="row">
                    <Button
                      disabled={decideDisabledReason !== null || busy}
                      aria-describedby={decideDisabledReason ? 'decide-gap' : undefined}
                      onClick={() => setMode('rejecting')}
                    >
                      Refuse
                    </Button>
                    <Button
                      variant="primary"
                      disabled={decideDisabledReason !== null || busy}
                      aria-describedby={decideDisabledReason ? 'decide-gap' : undefined}
                      onClick={() =>
                        void send(
                          '/reversal/approve',
                          {},
                          `${detail.ref} reversed. The mirror posting is on the ledger and points back at it.`,
                        )
                      }
                    >
                      {busy ? 'Posting…' : 'Approve the reversal'}
                    </Button>
                  </div>
                  {decideDisabledReason ? (
                    <p id="decide-gap" className="text-subtle" style={{ fontSize: '11.5px' }}>
                      {decideDisabledReason}
                    </p>
                  ) : (
                    <p className="text-subtle" style={{ fontSize: '11.5px' }}>
                      Approving posts the mirror immediately and stamps this request to it. The
                      player&rsquo;s cash can go negative — a deposit posted in error was never
                      theirs to spend, and refusing the correction because they have spent it would
                      leave the error standing forever.
                    </p>
                  )}
                </>
              )}
            </div>
          </Panel>
        </div>
      )}
    </Drawer>
  );
}
